/**
 * The cloud-production screen's decisions (PLAN §9, §10.2 screen 5, D11, D25):
 * the cost estimate the launch dialog shows, how a scheduled start typed into
 * a `datetime-local` input becomes the request's ISO time (and when the task
 * launches before it), and which statuses are still moving (the list polls
 * while any is). Pure; the components and the data layer import them.
 */
import {
  type CloudProduction,
  type CloudProductionStatus,
  type CloudTaskRole,
  type CloudTaskSize,
  type CloudTaskStatus,
  isTerminalCloudStatus,
  type LaunchCloudProductionRequest,
  MAX_CLOUD_PRODUCTION_MINUTES,
  PRELAUNCH_MINUTES,
} from '@acp/api-contract/cloudProductions.js';

/** The hours a cost estimate is quoted for, beside the hourly price ("$0.52 for 24 h"). */
export const ESTIMATE_HOURS = 24;

/** The production's price per hour: one task, or two when redundant (§9.3). Rounded to a millionth, as the contract does. */
export function hourlyEstimate(taskHourlyUsd: number, redundant: boolean): number {
  return Math.round(taskHourlyUsd * (redundant ? 2 : 1) * 1_000_000) / 1_000_000;
}

/** An hourly price: four decimals — a small task is about two cents an hour, and two decimals would hide the difference. */
export function formatHourlyUsd(usd: number): string {
  return `$${usd.toFixed(4)}`;
}

/** A total: whole cents. */
export function formatUsd(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

/** "≈ $0.0217/h; $0.52 for 24 h". */
export function describeEstimate(taskHourlyUsd: number, redundant: boolean): string {
  const hourly = hourlyEstimate(taskHourlyUsd, redundant);
  return `≈ ${formatHourlyUsd(hourly)}/h; ${formatUsd(hourly * ESTIMATE_HOURS)} for ${String(ESTIMATE_HOURS)} h`;
}

/**
 * A `datetime-local` value (`2026-09-26T18:30`, the browser's own zone) as the
 * ISO instant the contract takes, or null when it is empty or not a time.
 */
export function localInputToIso(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(value)) return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/** When the task of a start at `startIso` is launched: the contract's `PRELAUNCH_MINUTES` (§9.2.4) before it. */
export function launchTimeOf(startIso: string): Date {
  return new Date(new Date(startIso).getTime() - PRELAUNCH_MINUTES * 60_000);
}

/** `pending`, `starting`, `stopping`: the status will change without anyone asking — the list polls while one is. */
export function isTransitional(status: CloudProductionStatus): boolean {
  return status === 'pending' || status === 'starting' || status === 'stopping';
}

/** A production id shortened for a table cell: the ULID's last eight characters (its random part; the time part repeats). */
export function shortId(productionId: string): string {
  return productionId.slice(-8);
}

// ─── the table's rows ───

/** One task as the table shows it: the latest launch of its role. */
export type CloudTaskCell = {
  readonly role: CloudTaskRole;
  readonly status: CloudTaskStatus;
  readonly availabilityZone: string | null;
  readonly privateIp: string | null;
};

export type CloudProductionRow = {
  readonly productionId: string;
  readonly shortId: string;
  readonly studioName: string;
  /** `Rundown: Evening news` / `Playlist: Friday`. */
  readonly source: string;
  readonly status: CloudProductionStatus;
  readonly statusReason: string | null;
  readonly redundant: boolean;
  readonly rehearsal: boolean;
  readonly simulators: boolean;
  readonly taskSize: CloudTaskSize;
  /** `Starts 27.09 21:00 (launch 20:50)` for a scheduled one, `Launched 26.09 20:00` otherwise. */
  readonly schedule: string;
  /** When its node said it was ready, or null. */
  readonly ready: string | null;
  /** `$0.05` so far. */
  readonly accrued: string;
  /** `$0.0217/h`. */
  readonly hourly: string;
  readonly tasks: readonly CloudTaskCell[];
  /** Stop is offered: not stopping or over already, and the user may. */
  readonly stoppable: boolean;
};

/** Per role, the task of its latest launch (a relaunch after an unexpected stop adds a task, §9.2.6); main first. */
export function currentTasks(tasks: CloudProduction['tasks']): CloudTaskCell[] {
  const latest = new Map<CloudTaskRole, CloudProduction['tasks'][number]>();
  for (const task of tasks) {
    const seen = latest.get(task.role);
    if (seen === undefined || task.launch > seen.launch) latest.set(task.role, task);
  }
  return (['main', 'backup'] as const).flatMap((role) => {
    const task = latest.get(role);
    return task === undefined ? [] : [{ role, status: task.status, availabilityZone: task.availabilityZone, privateIp: task.privateIp }];
  });
}

/**
 * The table's row for one production. The names and the permission are the
 * view's (`studioName`, `source`, `mayStop`); `formatTime` is how the browser
 * shows an instant.
 */
export function cloudProductionRow(
  p: CloudProduction,
  context: { readonly studioName: string; readonly source: string; readonly mayStop: boolean; readonly formatTime: (iso: string) => string },
): CloudProductionRow {
  const { formatTime } = context;
  return {
    productionId: p.productionId,
    shortId: shortId(p.productionId),
    studioName: context.studioName,
    source: context.source,
    status: p.status,
    statusReason: p.statusReason,
    redundant: p.redundant,
    rehearsal: p.rehearsal,
    simulators: p.simulators,
    taskSize: p.taskSize,
    schedule: p.startAt === null ? `Launched ${formatTime(p.launchAt)}` : `Starts ${formatTime(p.startAt)} (launch ${formatTime(p.launchAt)})`,
    ready: p.readyAt === null ? null : formatTime(p.readyAt),
    accrued: formatUsd(p.cost.accruedUsd),
    hourly: `${formatHourlyUsd(p.cost.hourlyUsd)}/h`,
    tasks: currentTasks(p.tasks),
    stoppable: context.mayStop && p.status !== 'stopping' && !isTerminalCloudStatus(p.status),
  };
}

// ─── the launch dialog's request ───

/** What the launch dialog asks for: the launch request without its id (the mutation mints it). */
export type CloudLaunchRequest = Omit<LaunchCloudProductionRequest, 'productionId'>;

/** The safety stop's bounds, in hours: the contract's 15 minutes to 7 days (§1.1). */
export const MIN_SAFETY_HOURS = 0.25;
export const MAX_SAFETY_HOURS = MAX_CLOUD_PRODUCTION_MINUTES / 60;

/** The safety stop typed in hours, or null when it is not a number within the bounds. */
export function safetyStopHours(text: string): number | null {
  const hours = Number(text);
  return text.trim() !== '' && Number.isFinite(hours) && hours >= MIN_SAFETY_HOURS && hours <= MAX_SAFETY_HOURS ? hours : null;
}

/** The dialog's choices as they stand (ids resolved to what is shown selected; '' for none). */
export type LaunchChoices = {
  readonly studioId: string;
  /** The show style (D32); '': the studio's default. */
  readonly showStyleId: string;
  readonly kind: 'rundown' | 'playlist';
  readonly sourceId: string;
  /** false: start now. */
  readonly scheduled: boolean;
  /** The `datetime-local` value of a scheduled start. */
  readonly startLocal: string;
  readonly redundant: boolean;
  /** null while the sizes load. */
  readonly taskSize: CloudTaskSize | null;
  readonly rehearsal: boolean;
  readonly simulators: boolean;
  readonly safetyHoursText: string;
};

/** The request the choices make, or null while one is missing or invalid (the launch button is disabled). */
export function launchRequestOf(c: LaunchChoices): CloudLaunchRequest | null {
  const startAt = c.scheduled ? localInputToIso(c.startLocal) : 'now';
  const hours = safetyStopHours(c.safetyHoursText);
  if (c.studioId === '' || c.sourceId === '' || c.taskSize === null || startAt === null || hours === null) return null;
  return {
    studioId: c.studioId,
    ...(c.showStyleId === '' ? {} : { showStyleId: c.showStyleId }),
    rundownId: c.kind === 'rundown' ? c.sourceId : null,
    playlistId: c.kind === 'playlist' ? c.sourceId : null,
    startAt,
    redundant: c.redundant,
    taskSize: c.taskSize,
    rehearsal: c.rehearsal,
    simulators: c.simulators,
    maxDurationMinutes: Math.round(hours * 60),
  };
}
